import type { IsoDate } from "../domain/types.js";
import type { CheerRow } from "../../shared/types.js";
import { cheeredBy } from "../domain/supporter.js";

/**
 * 戳一下的樂觀更新。
 *
 * 按下去要立刻變成「已戳 ✓」，但寫入可能失敗——失敗時必須把按鈕改回去，
 * 否則使用者會以為戳到了。這段生命週期先前手寫在 main.js 的事件處理器裡，
 * 與 daily-log-store 是同一類風險（回滾錯了畫面就在說謊），卻只有瀏覽器
 * 測得到。
 *
 * 與每日紀錄的差別只在形狀：這裡是**新增一列**而不是改一列，所以回滾是把
 * 那一列拿掉，不是還原欄位。seam 的位置一樣——writer 由外部注入。
 *
 * **這個 module 只管寫入。** 「誰戳了哪天」「這個名字是不是我」是
 * `src/domain/supporter.ts` 的事——它們沒有狀態、不需要 writer、不涉及回滾，
 * 是對一個陣列的提問。先前這裡也有一份（`cheeredBy` / `namesOn`），但畫面
 * 拿不到它——跨進 props 的只有原始陣列，於是同一段邏輯在 `App.tsx` 又被寫了
 * 一遍。測試在測沒有人走的那條路。
 */

export interface CheerWriter {
  /** 送出一次戳。回傳值用不到——一人一天一次，寫進去就是那一列。 */
  save(date: IsoDate, nickname: string): Promise<unknown>;
}

export interface CheerStoreOptions {
  writer: CheerWriter;
  /** 資料有變、需要重繪。樂觀寫入與回滾都會觸發。 */
  onChange: () => void;
}

/**
 * 失敗的說明由呼叫端決定要放哪裡（toast 或畫面上的錯誤區），所以這裡
 * **回傳結果而不是自己回報**。
 */
export type CheerResult = { ok: true } | { ok: false; error: string };

export interface CheerStore {
  readonly cheers: CheerRow[];
  /** 從 API 載入一整批，覆蓋現有內容。 */
  replaceAll(rows: CheerRow[]): void;
  /** 戳一下。立刻反映在 cheers，失敗會自動回滾。 */
  cheer(date: IsoDate, nickname: string): Promise<CheerResult>;
}

export function createCheerStore({ writer, onChange }: CheerStoreOptions): CheerStore {
  let cheers: CheerRow[] = [];

  return {
    get cheers() {
      return cheers;
    },

    replaceAll(rows) {
      cheers = rows;
    },

    async cheer(date, nickname) {
      // 沒有名字就戳不了——後端也會擋，但這裡擋是為了不送一個註定失敗的請求
      if (!nickname) return { ok: false, error: "還沒有名字" };
      // 一人一天一次，已經戳過就不再送
      if (cheeredBy(cheers, date, nickname)) return { ok: true };

      const before = cheers;
      cheers = [...before, { cheer_date: date, nickname }];
      onChange();

      try {
        await writer.save(date, nickname);
        return { ok: true };
      } catch (error) {
        cheers = before;
        onChange();
        return { ok: false, error: error instanceof Error ? error.message : "戳失敗" };
      }
    }
  };
}
